/**
 * Connects game signals to SFX playback. Audio is a passive observer here —
 * it only subscribes to Rive events, so the game orchestrator stays unaware of
 * sound.
 *
 * Map: Rive event name → SFX clip base name (file in public/assets/baccarat_sfx/).
 *
 *   DrawCard         → DrawCard        a fresh card is dealt out
 *   DrawCardSingle   → DrawCardSingle  a single extra (3rd) card is drawn
 *   FlipCard         → FlipCard        a card is flipped face-up
 *   CollectCard      → CollectCard     cards are swept away at round end
 *   StartBet         → StartBet        "place your bets" popup (MainWinLose.riv)
 *   NoMoreBet        → NoMoreBet       "no more bets" popup (MainWinLose.riv)
 *   PlayerWin        → PlayerWin       player-win banner
 *   BankerWin        → BankerWinAndTie banker-win banner (shared banker/tie clip)
 *
 * Event names verified via `Tools/rive introspect`: card cues on baccarat.riv;
 * StartBet / NoMoreBet on MainWinLose.riv (the BetPanel popups emit them when
 * they play). Every Rive event is also logged as `[Rive][event:passthrough]`
 * (riveLayer.ts); if a sound never fires, check the console for the real name.
 *
 * The chip-denomination select sound (`ClickBetSelect`) is NOT here: the chip
 * picker and bet placement are DOM/state-driven, not Rive events, so main.ts
 * plays it from a GameState subscription. `CHIP_SELECT_SFX` is exported so it
 * gets preloaded with the rest.
 */
import type { RiveLayer } from '../rive/riveLayer';
import type { SoundManager } from './soundManager';

/** Rive event name → SFX clip base name. */
const EVENT_SFX: Readonly<Record<string, string>> = {
  DrawCard: 'DrawCard',
  DrawCardSingle: 'DrawCardSingle',
  FlipCard: 'FlipCard',
  CollectCard: 'CollectCard',
  StartBet: 'StartBet',
  NoMoreBet: 'NoMoreBet',
  PlayerWin: 'PlayerWin',
  BankerWin: 'BankerWinAndTie',
};

/** Clip played when a chip is selected or a bet is placed (driven from GameState). */
export const CHIP_SELECT_SFX = 'ClickBetSelect';

/** Distinct clips referenced anywhere — feed to SoundManager.preload(). */
export const SFX_CLIPS: readonly string[] = Array.from(
  new Set([...Object.values(EVENT_SFX), CHIP_SELECT_SFX]),
);

/** Subscribe SFX playback to the relevant Rive events. */
export function bindGameSounds(rive: RiveLayer, sound: SoundManager): void {
  for (const [event, clip] of Object.entries(EVENT_SFX)) {
    rive.on(event, () => sound.play(clip));
  }
}
